import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { showTelegramAlert } from '../services/telegramService';
import { useTranslation } from '../hooks/useTranslation';
import { useTelegramTheme, getInputClassesLarge } from '../hooks/useTelegramTheme';

const Settings = () => {
  const { user, updateUser } = useAuthStore();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTelegramTheme();
  const [activeTab, setActiveTab] = useState<'notifications' | 'profile'>('notifications');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'verify'>('email');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isConnected, setIsConnected] = useState(true); // Telegram connection status

  useEffect(() => {
    if (user) {
      setEmail((user as any).email || '');
    }
  }, [user]);

  // Check for tab query parameter on mount and when it changes
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'profile') {
      setActiveTab('profile');
      // Remove the query parameter after setting the tab
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendCode = async () => {
    if (!email.trim()) {
      showTelegramAlert(t('settings.enterEmailPlaceholder'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      showTelegramAlert(t('common.error'));
      return;
    }

    setSending(true);
    try {
      const { default: axios } = await import('axios');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('authToken');
      
      await axios.post(`${API_URL}/email-verification/send-code`, 
        { email: email.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      showTelegramAlert(t('settings.verificationSent'));
      setStep('verify');
      setCountdown(600); // 10 minutes
    } catch (error: any) {
      showTelegramAlert(error.response?.data?.message || t('settings.failedToSend'));
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    if (!code.trim()) {
      showTelegramAlert(t('settings.enterCode'));
      return;
    }

    setVerifying(true);
    try {
      const { default: axios } = await import('axios');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const token = localStorage.getItem('authToken');
      
      const response = await axios.post(`${API_URL}/email-verification/verify-code`, 
        { email: email.trim(), code: code.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      updateUser({ email: response.data.user.email });
      showTelegramAlert(t('settings.emailVerified'));
      setStep('email');
      setCode('');
    } catch (error: any) {
      showTelegramAlert(error.response?.data?.message || t('settings.invalidCode'));
    } finally {
      setVerifying(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="p-4 pb-24">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">
          {t('settings.title') || 'Settings'}
        </h1>
        <p className="text-sm text-gray-400">
          {t('settings.subtitle') || 'Customize your experience and manage your account details'}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-800">
        <button
          onClick={() => setActiveTab('notifications')}
          className={`flex items-center gap-2 px-4 py-3 font-semibold text-sm transition-all relative ${
            activeTab === 'notifications'
              ? 'text-green-500'
              : 'text-gray-400'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
          {t('settings.telegramNotifications') || 'Telegram Notifications'}
          {activeTab === 'notifications' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-500 rounded-t" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('profile')}
          className={`flex items-center gap-2 px-4 py-3 font-semibold text-sm transition-all relative ${
            activeTab === 'profile'
              ? 'text-green-500'
              : 'text-gray-400'
          }`}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          {t('settings.profile') || 'Profile'}
          {activeTab === 'profile' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-500 rounded-t" />
          )}
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'notifications' && (
        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-6 border border-gray-700/50">
          <p className="text-sm text-gray-400 mb-4">
            {t('settings.telegramNotificationsDesc') || 'Get instant updates via Telegram'}
          </p>
          <div className="text-sm text-gray-400 mb-6">
            {t('settings.uid') || 'UID'}: <span className="text-white font-semibold">{user?.username || user?.telegramId}</span>
          </div>
          <button
            onClick={() => setIsConnected(!isConnected)}
            className={`w-full py-4 rounded-xl font-bold text-base transition-all ${
              isConnected
                ? 'bg-red-600/80 hover:bg-red-600 text-white'
                : 'bg-green-600/80 hover:bg-green-600 text-white'
            }`}
          >
            {isConnected ? t('settings.connected') || 'Connected' : t('settings.connect') || 'Connect'}
          </button>
        </div>
      )}

      {activeTab === 'profile' && (
        <div className="space-y-4">
          {/* Email Verification Section */}
          {step === 'email' ? (
            <>
              <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-6 border border-gray-700/50">
                <div className="flex items-center gap-2 mb-4">
                  <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-gray-400">
                    {t('settings.enterCodeDesc') || 'Enter your email to receive verification code'}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-3">
                    {t('settings.emailAddress') || 'Email Address'}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('settings.enterEmailPlaceholder') || 'Enter your email'}
                    className={getInputClassesLarge(theme)}
                  />
                </div>
              </div>

              <button
                onClick={handleSendCode}
                disabled={sending}
                className="w-full py-4 bg-gradient-to-r from-primary to-yellow-500 text-black rounded-2xl text-base font-bold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02] transition-all active:scale-95 disabled:opacity-60 disabled:hover:scale-100"
              >
                {sending ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('settings.sending') || 'Sending...'}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    {t('settings.sendVerificationCode') || 'Send Verification Code'}
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-6 border border-gray-700/50">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm text-gray-400">
                      {email}
                    </p>
                  </div>
                  {countdown > 0 && (
                    <span className="text-sm text-primary font-mono">
                      {formatTime(countdown)}
                    </span>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-3">
                    {t('settings.verificationCode') || 'Verification Code'}
                  </label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className={`${getInputClassesLarge(theme)} text-2xl text-center font-mono tracking-widest`}
                  />
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    {t('settings.enterCodeDesc') || 'Enter the 6-digit code sent to your email'}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mb-4">
                <button
                  onClick={() => {
                    setStep('email');
                    setCode('');
                  }}
                  className="flex-1 py-3 bg-gray-800/50 hover:bg-gray-700/50 text-white rounded-xl text-sm font-semibold transition-all"
                >
                  {t('settings.changeEmail') || 'Change Email'}
                </button>
                <button
                  onClick={handleSendCode}
                  disabled={countdown > 540}
                  className="flex-1 py-3 bg-blue-600/50 hover:bg-blue-600/70 text-white rounded-xl text-sm font-semibold transition-all disabled:opacity-50"
                >
                  {t('settings.resendCode') || 'Resend Code'}
                </button>
              </div>

              <button
                onClick={handleVerify}
                disabled={verifying || code.length !== 6}
                className="w-full py-4 bg-gradient-to-r from-primary to-yellow-500 text-black rounded-2xl text-base font-bold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02] transition-all active:scale-95 disabled:opacity-60 disabled:hover:scale-100"
              >
                {verifying ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('settings.verifying') || 'Verifying...'}
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {t('settings.verifyEmail') || 'Verify Email'}
                  </>
                )}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Settings;
